import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateInput, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import {
  parseDateLocalToUTC,
  type LegalObligationEvidenceKindValue,
  type LegalObligationIntervalVariantView,
  type LegalObligationPendingResultView,
} from '@safetyhub/shared';
import { EditModalFooter } from '../common/EditModalFooter';
// 오늘(현장 시간대 yyyy-MM-dd) — `DateInput`의 `max`와 기본값(2026-11-03 P2 — 종전 브라우저 로컬)
import { todayDateInputInTimezone } from '../../lib/localDate';
import {
  getLegalObligationEvidenceCandidates,
  legalObligationEvidenceQueryKeys,
} from '../../lib/legalObligationEvidence';
import { describeMeasurementResult, EVIDENCE_KIND_LABEL } from '../../lib/legalDocumentLabels';

/** 서버 DTO의 메모 상한(`LEGAL_OBLIGATION_EVIDENCE_NOTE_MAX`)과 같은 값 — 화면 쪽 안전선 */
const NOTE_MAX_LENGTH = 500;

/** 제출 형상 — 종류마다 실리는 칸이 다르다(서버가 `kind` ↔ 포인터 짝을 집행한다) */
export type EvidenceSubmitInput =
  | { kind: 'INTERNAL_REPORT'; reportId: string; fulfilledAt: string; note?: string; resultEntryId?: string }
  | { kind: 'NO_EVENT_IN_PERIOD'; fulfilledAt: string; note: string }
  | { kind: 'EXTERNAL_FILE'; file: File; fulfilledAt: string; note?: string; resultEntryId?: string; alsoInstanceIds?: string[] };

/** 같은 파일을 함께 등록할 수 있는 다른 항목(Q14 ⓐ — 같은 의무의 다른 사건·대상) */
export interface EvidenceSharedTarget {
  instanceId: string;
  label: string;
}

export interface EvidenceModalProps {
  open: boolean;
  companyId: string;
  siteId: string;
  obligationId: string;
  obligationTitle: string;
  /** 우리 양식이 걸려 있나 — 없으면 「SafetyHub 문서」 선택지를 보이지 않는다(후보가 구조적으로 0이다) */
  templateAvailable: boolean;
  /**
   * 「해당 기간 사건 없음」 선택지를 보이나 — 사건 등록으로 생긴 항목(9-i)이면 false(사건이 «있었다»는 항목에 그 기록은
   * 모순이라 서버도 400). 생략하면 true(종전 동작).
   */
  allowNoEvent?: boolean;
  /**
   * 결과표가 아직 없는 측정 결과(B+ · 서버 `pendingResults` — 측정일 내림차순). 생략하면 빈 배열.
   * 1건이면 서버가 자동으로 그 결과에 잇는다(안내만) · 2건 이상이면 «어느 측정의 결과표인지» 고르는 것이 필수다(서버도 400).
   * 「사건 없음」은 결과와 잇지 않는다
   */
  pendingResults?: readonly LegalObligationPendingResultView[];
  /** 결과 이름을 찾는 변형 줄(`row.intervalVariants`) — `describeMeasurementResult` */
  resultVariants?: ReadonlyArray<Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>>;
  /** 의무 이름 뒤 안내문 — 생략하면 「이번 기간의 문서」 문구(결과 단위 입구가 결과표 문구로 바꾼다 · B+ 2차) */
  introText?: string;
  /**
   * 현장 유효 시간대(IANA · 서버 `LegalObligationsResponse.timezone`) — 이행일 날짜 → UTC 변환과 「오늘」(`max`·초기값)이 이 시간대다
   * (2026-11-03 P2). 서버가 기한을 이 시간대로 파생하므로 브라우저 로컬을 쓰면 다른 시간대 현장에서 「늦었나」·「미래인가」가 하루 갈린다
   */
  timezone: string;
  /**
   * 외부 파일을 **함께 등록할 수 있는 다른 항목**(사용자 결정 2026-11-04 Q14 ⓐ — 자율안전확인 신고증명서는 형식 단위라 한 장이 같은 형식의 여러
   * 대를 덮는다). 같은 의무의 아직 문서가 없는 다른 사건 항목이다(서버도 같은 현장·같은 의무만 받는다). 생략·빈 배열이면 칸을 그리지 않는다.
   * 측정 결과표는 한 측정의 문서라 결과와 잇는 경우(`pendingResults`가 있을 때)에는 그리지 않는다(서버도 400)
   */
  sharedTargets?: readonly EvidenceSharedTarget[];
  submitting: boolean;
  /** 서버 거부 문구 — 모달을 연 채로 보인다(닫으면 무엇이 틀렸는지 잃는다) */
  errorMessage: string | null;
  onSubmit: (input: EvidenceSubmitInput) => void;
  onClose: () => void;
}

/**
 * 날짜만 고른 이행일을 UTC ISO로 — 시간대 정책(CLAUDE.md): 날짜만 입력받아도 **현장 시간대 00:00:00**을 붙여 UTC로
 * 보낸다. 기한(`periodEnd`)은 현장 시간대의 23:59:59 UTC 환산이라, 기한 당일 00:00은 기한 «안»이고 다음 날
 * 00:00은 «밖»이다 — 그것이 「늦었나」 판정과 맞는 경계다. ⛔ 종전엔 **브라우저 로컬** 00:00이었다(2026-11-03 P2) — 서버 기한과
 * 시간대가 다르면 그 경계가 하루 어긋났다. `timezone`은 서버 응답의 현장 유효 시간대(`LegalObligationsResponse.timezone`)다
 */
export function fulfilledAtToIso(localDate: string, timezone: string): string {
  return parseDateLocalToUTC(localDate, timezone).toISOString();
}

/**
 * 증빙 붙이기(9-d) — 세 종류를 한 모달에서 고른다: SafetyHub 문서 연결(L3) · 외부 발급 파일 업로드(L5) ·
 * 「해당 기간 사건 없음」 기록(L13). 이행일은 **소급 무제한**(L7 — 과거 창을 두지 않는다)이고 미래만 막는다
 * (`max`가 오늘). 서버가 같은 판정을 한 번 더 한다(여기는 편의이지 벽이 아니다).
 *
 * 후보 문서는 서버가 이미 **같은 현장 · 결재 완료 · 그 의무의 양식**으로 좁혀 준다 — 화면이 다른 현장의
 * 문서를 고를 길이 없다. 이미 다른 기간에 붙은 문서는 경고를 보이되 막지 않는다(같은 문서가 두 기간을
 * 덮는 설계가 있을 수 있고 그 판정은 사람 몫이다).
 */
export default function EvidenceModal({
  open,
  companyId,
  siteId,
  obligationId,
  obligationTitle,
  templateAvailable,
  allowNoEvent = true,
  pendingResults = [],
  resultVariants = [],
  introText,
  timezone,
  sharedTargets = [],
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: EvidenceModalProps) {
  const [kind, setKind] = useState<LegalObligationEvidenceKindValue>(
    templateAvailable ? 'INTERNAL_REPORT' : 'EXTERNAL_FILE'
  );
  const [reportId, setReportId] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const today = todayDateInputInTimezone(timezone);
  const [fulfilledAt, setFulfilledAt] = useState(today);
  const [note, setNote] = useState('');
  /** B+ — 고른 측정 결과(2건 이상일 때만 고른다 · 1건은 서버가 자동 연결) */
  const [resultEntryId, setResultEntryId] = useState<string | null>(null);
  const linksResult = kind !== 'NO_EVENT_IN_PERIOD';
  const mustChooseResult = linksResult && pendingResults.length > 1;
  /** Q14 ⓐ — 같은 파일을 함께 등록할 다른 항목(외부 파일 · 결과표가 아닐 때만) */
  const [alsoInstanceIds, setAlsoInstanceIds] = useState<string[]>([]);
  const showShared = kind === 'EXTERNAL_FILE' && sharedTargets.length > 0 && pendingResults.length === 0;

  const candidatesQuery = useQuery({
    queryKey: legalObligationEvidenceQueryKeys.candidates(companyId, siteId, obligationId),
    queryFn: () => getLegalObligationEvidenceCandidates(companyId, siteId, obligationId),
    enabled: open && kind === 'INTERNAL_REPORT',
  });
  const candidates = candidatesQuery.data?.candidates ?? [];

  const trimmedNote = note.trim();
  const isDirty =
    reportId !== null || file !== null || resultEntryId !== null || trimmedNote !== '' || fulfilledAt !== today || alsoInstanceIds.length > 0;

  const blockedReason = !fulfilledAt
    ? '이행일을 선택하세요.'
    : kind === 'INTERNAL_REPORT' && !reportId
      ? '연결할 문서를 선택하세요.'
      : kind === 'EXTERNAL_FILE' && !file
        ? '업로드할 파일을 선택하세요.'
        : kind === 'NO_EVENT_IN_PERIOD' && !trimmedNote
          ? '무엇이 없었는지 메모를 적으세요.'
          : mustChooseResult && !resultEntryId
            ? '어느 측정의 결과표인지 고르세요.'
            : '';

  const handleSubmit = () => {
    if (blockedReason || submitting) return;
    const iso = fulfilledAtToIso(fulfilledAt, timezone);
    const noteField = trimmedNote ? { note: trimmedNote } : {};
    const resultField = mustChooseResult && resultEntryId ? { resultEntryId } : {};
    if (kind === 'INTERNAL_REPORT' && reportId) {
      onSubmit({ kind, reportId, fulfilledAt: iso, ...noteField, ...resultField });
    } else if (kind === 'EXTERNAL_FILE' && file) {
      const sharedField = showShared && alsoInstanceIds.length > 0 ? { alsoInstanceIds } : {};
      onSubmit({ kind, file, fulfilledAt: iso, ...noteField, ...resultField, ...sharedField });
    } else if (kind === 'NO_EVENT_IN_PERIOD') {
      onSubmit({ kind, fulfilledAt: iso, note: trimmedNote });
    }
  };

  const kinds: LegalObligationEvidenceKindValue[] = [
    ...(templateAvailable ? (['INTERNAL_REPORT'] as const) : []),
    'EXTERNAL_FILE',
    ...(allowNoEvent ? (['NO_EVENT_IN_PERIOD'] as const) : []),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="문서 등록"
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="문서 등록"
          submitDisabled={!!blockedReason || submitting}
          busy={submitting}
          busyLabel="등록 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {introText ??
            '에 이번 기간의 문서를 등록합니다. 문서가 등록되면 그 기간이 닫히고, 이행일이 기한을 넘겼으면 「늦은 완료」로 남습니다.'}
        </p>

        <fieldset>
          <legend className={FORM_LABEL_CLASS}>종류</legend>
          <div className="space-y-2">
            {kinds.map((value) => {
              const checked = kind === value;
              return (
                <label
                  key={value}
                  className={`flex cursor-pointer items-start gap-3 rounded-[10px] border px-3 py-2.5 ${
                    checked ? 'border-brand-500 bg-brand-50' : 'border-line hover:bg-surface-hover'
                  }`}
                >
                  <input
                    type="radio"
                    name="legal-evidence-kind"
                    value={value}
                    checked={checked}
                    onChange={() => setKind(value)}
                    disabled={submitting}
                    className="mt-1 accent-brand-600"
                  />
                  <span className="block text-sm font-bold text-ink-900">{EVIDENCE_KIND_LABEL[value]}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {kind === 'INTERNAL_REPORT' && (
          <fieldset>
            <legend className={FORM_LABEL_CLASS}>연결할 문서</legend>
            {candidatesQuery.isLoading ? (
              <p className="text-sm text-ink-500" role="status">
                문서를 불러오는 중...
              </p>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-ink-500">
                이 현장에서 이 문서명의 양식으로 결재 완료된 문서가 없습니다. 문서를 먼저 작성·결재하세요.
              </p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto">
                {candidates.map((c) => (
                  <li key={c.reportId}>
                    <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-line px-3 py-2 hover:bg-surface-hover">
                      <input
                        type="radio"
                        name="legal-evidence-report"
                        value={c.reportId}
                        checked={reportId === c.reportId}
                        onChange={() => setReportId(c.reportId)}
                        disabled={submitting}
                        className="mt-1 accent-brand-600"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink-900">{c.title}</span>
                        {c.alreadyAttachedToInstanceId && (
                          <span className="block text-xs text-amber-700">이미 다른 기간에 등록된 문서입니다.</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        )}

        {kind === 'EXTERNAL_FILE' && (
          <div>
            <label htmlFor="legal-evidence-file" className={FORM_LABEL_CLASS}>
              파일 (PDF·이미지·오피스 문서, 10MB 이하)
            </label>
            <input
              id="legal-evidence-file"
              type="file"
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={submitting}
              className="block w-full text-sm text-ink-700"
            />
          </div>
        )}

        {showShared && (
          <fieldset data-testid="evidence-shared-targets">
            <legend className={FORM_LABEL_CLASS}>같은 파일을 함께 등록할 항목 (선택)</legend>
            <p className="mb-1 text-xs text-ink-500">
              자율안전확인 신고증명서처럼 한 장이 같은 형식의 여러 대를 덮는 문서면 함께 고르세요. 파일은 한 번만 올라가고 항목마다 같은 문서로 남습니다.
            </p>
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {sharedTargets.map((t) => (
                <li key={t.instanceId}>
                  <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-line px-3 py-2 hover:bg-surface-hover">
                    <input
                      type="checkbox"
                      checked={alsoInstanceIds.includes(t.instanceId)}
                      onChange={(e) =>
                        setAlsoInstanceIds((prev) =>
                          e.target.checked ? [...prev, t.instanceId] : prev.filter((id) => id !== t.instanceId),
                        )
                      }
                      disabled={submitting}
                      className="mt-1 accent-brand-600"
                    />
                    <span className="text-sm text-ink-900">{t.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}

        {linksResult && pendingResults.length === 1 && (
          <p className="rounded-[10px] border border-line bg-surface-hover px-3 py-2 text-sm text-ink-700" role="note">
            이 문서는 <span className="font-bold">{describeMeasurementResult(pendingResults[0], resultVariants)}</span> 측정의
            결과표로 등록됩니다. 그 측정이 든 기간의 기록으로 남습니다.
          </p>
        )}

        {mustChooseResult && (
          <fieldset>
            <legend className={FORM_LABEL_CLASS}>어느 측정의 결과표인가요? (필수)</legend>
            <ul className="space-y-1">
              {pendingResults.map((r) => (
                <li key={r.entryId}>
                  <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-line px-3 py-2 hover:bg-surface-hover">
                    <input
                      type="radio"
                      name="legal-evidence-result"
                      value={r.entryId}
                      checked={resultEntryId === r.entryId}
                      onChange={() => setResultEntryId(r.entryId)}
                      disabled={submitting}
                      className="mt-1 accent-brand-600"
                    />
                    <span className="text-sm text-ink-900">{describeMeasurementResult(r, resultVariants)}</span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-ink-500">
              결과표가 없는 측정이 여러 건입니다. 측정마다 결과표를 따로 등록하세요 — 고른 측정이 든 기간의 기록으로 남습니다.
            </p>
          </fieldset>
        )}

        <div>
          <label htmlFor="legal-evidence-fulfilled-at" className={FORM_LABEL_CLASS}>
            이행일
          </label>
          <DateInput
            id="legal-evidence-fulfilled-at"
            label="이행일"
            value={fulfilledAt}
            onChange={setFulfilledAt}
            max={today}
            disabled={submitting}
            className="w-full"
          />
          <p className="mt-1 text-xs text-ink-500">
            실제로 이행한 날짜입니다(오늘 올려도 지난달에 한 일이면 그 날짜로). 소급에 제한은 없습니다.
          </p>
        </div>

        <div>
          <label htmlFor="legal-evidence-note" className={FORM_LABEL_CLASS}>
            {kind === 'NO_EVENT_IN_PERIOD' ? '메모 (필수 — 무엇이 없었나)' : '메모 (선택)'}
          </label>
          <textarea
            id="legal-evidence-note"
            className={FORM_TEXTAREA_CLASS}
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            placeholder={
              kind === 'NO_EVENT_IN_PERIOD'
                ? '예: 이번 달 신규 입사자 없음'
                : '예: 2025년 9월 교육을 10월에 기록(소급)'
            }
          />
        </div>

        {errorMessage && (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
